/**
 * Alaine's Hue™ — Natural Color Reformulator (COMING SOON)
 * ─────────────────────────────────────────────────────────
 * Forward-looking module for Alaine's Bench. Captures the FDA dye phase-out
 * market opportunity (Red 3 by 2027, RFK/HHS phasing out 6 more dyes).
 *
 * Right now this page is a teaser / "Under Construction" landing that:
 *   1. Anchors Alaine as the food scientist who saw this coming
 *   2. Validates demand via the "Notify me" capture
 *   3. Becomes shareable marketing for LinkedIn / Slack
 *
 * Build-out into a full reformulation tool is parked until 30-60 days of
 * Alaine's Bench beta feedback validates multiple users complaining about
 * dye reformulation. (See /app/memory/COLOR_REFORMULATOR_PRD.md)
 */
import React, { useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Palette, Warning, ArrowRight, Check, Wrench, Flask } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

// ─────────────────────────────────────────────────────────────────────────
// Dye → Natural alternative mapping with REPLACEMENT RATIOS.
//
// Synthetic FD&C dyes are extremely concentrated and shelf-stable. Naturals
// require higher use rates to match visual intensity. Ratios below are the
// industry-standard "approximate weight equivalent" to match dye color
// intensity in a typical application. Real reformulation work requires ΔE
// color matching against a Pantone or lab reference.
//
// Use rates are mg active per kg finished product (ppm). Ratios are
// "X times the weight of dye" needed to match.
//
// Sources: Sensient Colors application data, DD Williamson tech sheets,
// GNT EXBERRY application guides, FDA 21 CFR 73 / 74 color additive lists.
// ─────────────────────────────────────────────────────────────────────────
const DYE_SWAPS = [
  {
    dye: "FD&C Red No. 3",
    dyeHex: "#E0203A",
    aka: "Erythrosine",
    status: "BANNED — must remove by Jan 2027",
    typicalUseRate: "10–50 ppm (10–50 mg/kg)",
    naturals: [
      { name: "Beet Juice Concentrate (E162)", hex: "#A4253C", ratio: "8–20× by weight",
        note: "Heat sensitive >180°F · pH 3–6 only · use rate 80–1,000 ppm" },
      { name: "Anthocyanin — Black Carrot (E163)", hex: "#7D1F36", ratio: "5–12× by weight",
        note: "Best pH 2.5–4.5 · stable to heat · use rate 50–600 ppm" },
      { name: "Lycopene — Tomato Oleoresin (E160d)", hex: "#C42D1E", ratio: "10–30× by weight",
        note: "Oil-soluble only · stable · use rate 100–1,500 ppm" },
    ],
    labMade: { name: "Nature-Identical Lycopene (DSM/BASF synth)", hex: "#C42D1E",
      ratio: "3–8× by weight",
      note: "FDA approved (21 CFR 73.585). Synthesized identical to tomato. More stable, cheaper, water-dispersible forms. 100–300 ppm." },
  },
  {
    dye: "FD&C Red No. 40",
    dyeHex: "#E5392E",
    aka: "Allura Red",
    status: "Phase-out 2026–2027",
    typicalUseRate: "50–200 ppm",
    naturals: [
      { name: "Carmine (Cochineal, E120)", hex: "#B0233A", ratio: "3–8× by weight",
        note: "Not vegan / not kosher · stable across pH 2–7 · 150–1,500 ppm" },
      { name: "Lycopene (Tomato Oleoresin)", hex: "#C42D1E", ratio: "10–25× by weight",
        note: "Oil-soluble · heat stable · 500–2,000 ppm" },
      { name: "Paprika Oleoresin (E160c)", hex: "#CE4A1B", ratio: "5–15× by weight",
        note: "Pushes orange-red · stable · 250–1,200 ppm" },
    ],
    labMade: { name: "Nature-Identical Lycopene + Beta-Carotene blend",
      hex: "#C42D1E", ratio: "4–10× by weight",
      note: "Custom blend ratio to hit allura-red shade. DSM Vegex / BASF Lucarotin. 200–600 ppm." },
  },
  {
    dye: "FD&C Yellow No. 5",
    dyeHex: "#F9DB17",
    aka: "Tartrazine",
    status: "Phase-out 2026–2027",
    typicalUseRate: "50–150 ppm",
    naturals: [
      { name: "Turmeric Oleoresin (E100)", hex: "#E8C111", ratio: "3–10× by weight",
        note: "Light sensitive — package opaque · pH stable 3–7 · 200–1,000 ppm" },
      { name: "Annatto Extract (E160b)", hex: "#E6A12C", ratio: "5–12× by weight",
        note: "Pushes orange-yellow · mild allergen flag · 100–600 ppm" },
      { name: "Riboflavin (E101)", hex: "#F8D62A", ratio: "20–40× by weight",
        note: "Natural OR nature-identical · stable to heat · 100–500 ppm" },
    ],
    labMade: { name: "Nature-Identical Riboflavin / β-Carotene",
      hex: "#F4CC25", ratio: "5–15× by weight",
      note: "Riboflavin (B2) synthesized = identical to fermented. FDA approved. β-Carotene synth (DSM, BASF) for warmer yellow. 100–400 ppm." },
  },
  {
    dye: "FD&C Yellow No. 6",
    dyeHex: "#F3922F",
    aka: "Sunset Yellow",
    status: "Phase-out 2026–2027",
    typicalUseRate: "50–150 ppm",
    naturals: [
      { name: "Beta-Carotene — extracted (E160a)", hex: "#E8841B", ratio: "4–10× by weight",
        note: "Heat & oxygen sensitive · oil-dispersible · 200–800 ppm" },
      { name: "Paprika Oleoresin", hex: "#D8762A", ratio: "5–12× by weight",
        note: "Robust across pH 3–7 · stable · 300–1,200 ppm" },
      { name: "Annatto Extract", hex: "#E18A2A", ratio: "6–14× by weight",
        note: "Strong orange — declare allergen · 150–700 ppm" },
    ],
    labMade: { name: "Nature-Identical β-Carotene (DSM Lucarotin / BASF Lucantin)",
      hex: "#E8841B", ratio: "3–7× by weight",
      note: "Synthesized identical to carrot/algae. Water-dispersible beadlet forms. FDA approved (21 CFR 73.95). 80–300 ppm." },
  },
  {
    dye: "FD&C Blue No. 1",
    dyeHex: "#0E83CB",
    aka: "Brilliant Blue",
    status: "Phase-out 2026–2027",
    typicalUseRate: "5–50 ppm",
    naturals: [
      { name: "Spirulina Extract — Phycocyanin (E162a)", hex: "#1E6FA5",
        ratio: "30–80× by weight",
        note: "Heat sensitive >170°F · pH 5–7 only · 200–2,000 ppm · light-sensitive" },
      { name: "Butterfly Pea Flower Extract", hex: "#2A5BA3", ratio: "20–50× by weight",
        note: "pH shifts — acid turns purple/pink · 150–1,500 ppm" },
      { name: "Huito (Genipa americana)", hex: "#1A56A0", ratio: "15–40× by weight",
        note: "Emerging natural blue, heat stable, FDA petition pending" },
    ],
    labMade: { name: "No commercial nature-identical synth currently available",
      hex: "#9CA3AF", ratio: "N/A",
      note: "Phycocyanin protein cannot be cost-effectively synthesized at scale yet. Encapsulation tech (e.g., Chr. Hansen Cevitae) improves spirulina heat tolerance — still the only path." },
  },
  {
    dye: "FD&C Blue No. 2",
    dyeHex: "#1B3C8F",
    aka: "Indigotine",
    status: "Phase-out 2026–2027",
    typicalUseRate: "50–200 ppm",
    naturals: [
      { name: "Anthocyanin — Red Cabbage (alkaline pH)", hex: "#2F3F86",
        ratio: "50–150× by weight",
        note: "Requires pH-shift formula (alkaline buffer system) · 500–4,000 ppm" },
      { name: "Spirulina + Anthocyanin blend", hex: "#244678", ratio: "40–100× by weight",
        note: "Two-component system · complex formulation work" },
      { name: "Butterfly Pea + Indigo Carmine adjacent", hex: "#2B4E94", ratio: "30–80× by weight",
        note: "Use butterfly pea at neutral pH only" },
    ],
    labMade: { name: "No commercial nature-identical synth currently available",
      hex: "#9CA3AF", ratio: "N/A",
      note: "Anthocyanins are too structurally complex for cost-effective synthesis at food scale. Encapsulated forms (GNT, EXBERRY) extend stability but ratio holds." },
  },
  {
    dye: "FD&C Green No. 3",
    dyeHex: "#00A86B",
    aka: "Fast Green FCF",
    status: "Phase-out 2026–2027",
    typicalUseRate: "50–200 ppm",
    naturals: [
      { name: "Spirulina + Turmeric blend (custom)", hex: "#1F9D5E",
        ratio: "40–100× by weight",
        note: "Tune blend ratio per shade · 400–3,000 ppm of finished blend" },
      { name: "Chlorophyll Copper Complex (E141)", hex: "#2C8D58", ratio: "8–25× by weight",
        note: "Stable, slightly muted vs FD&C Green · 200–1,500 ppm · check Cu limits per FDA" },
      { name: "Chlorophyllin (sodium copper)", hex: "#358C61", ratio: "10–30× by weight",
        note: "Water-soluble · brighter than free chlorophyll · 150–1,200 ppm" },
    ],
    labMade: { name: "Nature-Identical Chlorophyllin (sodium copper)",
      hex: "#358C61", ratio: "5–15× by weight",
      note: "Cu-Chlorophyllin (21 CFR 73.125) — semi-synthetic, derived & stabilized. Brighter green at lower use. 100–500 ppm." },
  },
];

// Brands publicly transitioning (text only — no logos to avoid trademark issues)
const TRANSITIONING_BRANDS = [
  "Kraft Mac & Cheese",
  "Skittles",
  "Lucky Charms",
  "Trix",
  "Froot Loops",
  "M&M's (color reformulation)",
  "Mountain Dew",
  "Gatorade",
  "Hostess (snack cakes)",
  "Pop-Tarts (school-channel)",
  "Tyson (kids' meal portfolio)",
  "Heinz (kids' ketchup)",
];

const STABILITY_MATRIX = [
  { color: "Beet Juice",         pH: "3–6",  heat: "Sensitive",  light: "Sensitive",     cost: "5–8x dye" },
  { color: "Anthocyanin (black carrot)", pH: "2–4.5", heat: "Stable",  light: "Moderate", cost: "8–12x dye" },
  { color: "Lycopene",           pH: "3–8",  heat: "Stable",     light: "Stable",        cost: "10–15x dye" },
  { color: "Carmine",            pH: "2–7",  heat: "Stable",     light: "Stable",        cost: "12–20x dye" },
  { color: "Turmeric",           pH: "3–7",  heat: "Stable",     light: "Sensitive",     cost: "4–7x dye" },
  { color: "Annatto",            pH: "3–9",  heat: "Stable",     light: "Stable",        cost: "6–10x dye" },
  { color: "Beta-Carotene",      pH: "3–8",  heat: "Sensitive",  light: "Sensitive",     cost: "8–14x dye" },
  { color: "Paprika Oleoresin",  pH: "3–7",  heat: "Stable",     light: "Stable",        cost: "5–9x dye" },
  { color: "Spirulina",          pH: "5–7",  heat: "Sensitive",  light: "Moderate",      cost: "12–18x dye" },
  { color: "Butterfly Pea",      pH: "shifts", heat: "Moderate", light: "Stable",        cost: "15–22x dye" },
  { color: "Chlorophyll Cu",     pH: "5–9",  heat: "Stable",     light: "Stable",        cost: "10–14x dye" },
];

// ─────────────────────────────────────────────────────────────────────────
// SNACK APPLICATION GALLERY
// Side-by-side color palettes — synthetic-dye original vs the natural-color
// reformulated equivalent. Hex values approximate real-world shifts (e.g.,
// Mars Wrigley's 2026 natural Skittles announcement; General Mills' 2017
// dye-free Trix). NOT brand photography — just color palettes styled like
// each application (gum-drops, cereal loops, dragees, etc.) so a founder
// can immediately see what their finished product will look like after
// reformulation.
// ─────────────────────────────────────────────────────────────────────────
const SNACK_APPLICATIONS = [
  {
    category: "Hard-Shell Candy",
    examples: "Skittles · M&M's · Reese's Pieces · Sixlets",
    realWorld: "Mars Wrigley announced dye-free Skittles + M&M's + Starburst launching 2026 across North America (Apr 2025)",
    shape: "circle",
    note: "Hard-shell dragees show color most dramatically — synthetic dyes are 'printer-cartridge bright' vs. naturals at 70–80% saturation. Mars reformulation testers' #1 complaint: lost neon green and electric blue.",
    synthetic: [
      { name: "Red 40 red",    hex: "#E5392E" },
      { name: "Yellow 5+6 orange", hex: "#F3922F" },
      { name: "Yellow 5 yellow", hex: "#F9DB17" },
      { name: "Yellow 5 + Blue 1 green", hex: "#3DAA48" },
      { name: "Blue 1 blue",    hex: "#0E83CB" },
      { name: "Red 40 + Blue 1 purple", hex: "#7E3FA8" },
    ],
    natural: [
      { name: "Beet/Lycopene red", hex: "#B7314A" },
      { name: "Paprika/β-carotene orange", hex: "#D67A2A" },
      { name: "Turmeric yellow",    hex: "#E8C111" },
      { name: "Chlorophyll-Cu + turmeric green", hex: "#5A9C4C" },
      { name: "Spirulina blue",     hex: "#1E6FA5" },
      { name: "Anthocyanin purple", hex: "#6B3982" },
    ],
  },
  {
    category: "Cereal Loops",
    examples: "Froot Loops · Trix · Lucky Charms marshmallows",
    realWorld: "General Mills reformulated Trix dye-free in 2017 using turmeric, annatto, fruit & vegetable juices — relaunched original after sales drop. Now reformulating again 2025–2027.",
    shape: "loop",
    note: "Cereal pieces are extruded then sprayed/tumbled with color — natural colors bleed slightly during the milk soak, so retention has to be tested at 60 sec, 3 min, 5 min in milk.",
    synthetic: [
      { name: "Red 40 cherry", hex: "#E0303A" },
      { name: "Yellow 6 orange", hex: "#F08D1E" },
      { name: "Yellow 5 lemon", hex: "#F5D820" },
      { name: "Yellow 5 + Blue 1 lime", hex: "#5BB23B" },
      { name: "Blue 1 berry", hex: "#0F88CC" },
      { name: "Blue 2 grape", hex: "#5A3FAE" },
    ],
    natural: [
      { name: "Black carrot anthocyanin red", hex: "#A52A3F" },
      { name: "Annatto orange", hex: "#D27A1B" },
      { name: "Turmeric yellow", hex: "#E5C424" },
      { name: "Spirulina + turmeric lime", hex: "#789F3C" },
      { name: "Spirulina blue", hex: "#256FA4" },
      { name: "Red cabbage anthocyanin purple", hex: "#6C3C7B" },
    ],
  },
  {
    category: "Gummies / Pâte de Fruit",
    examples: "Sour Patch Kids · Haribo · Welch's Fruit Snacks · Trolli",
    realWorld: "Black Forest Organic Gummy Bears (vegan, dye-free since 2020) — uses carrot, beetroot, spirulina, turmeric. Annie's Organic Bunnies similar.",
    shape: "drop",
    note: "Gummies are gelatin or pectin matrices at pH 3.5–4.0 — anthocyanins THRIVE in this pH band. Reds and purples reformulate beautifully. Blues and greens are the hardest because phycocyanin denatures above 170°F during cook stage.",
    synthetic: [
      { name: "Red 40 cherry",   hex: "#E0303A" },
      { name: "Yellow 6 orange", hex: "#F08D1E" },
      { name: "Yellow 5 lemon",  hex: "#F5D820" },
      { name: "Y5 + B1 apple",   hex: "#5BB23B" },
      { name: "Blue 1 raspberry", hex: "#1F7FB8" },
    ],
    natural: [
      { name: "Black carrot red", hex: "#A82A3F" },
      { name: "Carrot + paprika orange", hex: "#D27A1B" },
      { name: "Turmeric lemon", hex: "#E8C424" },
      { name: "Apple + spirulina green", hex: "#6FA042" },
      { name: "Spirulina blue (encapsulated)", hex: "#2972A8" },
    ],
  },
  {
    category: "Snack Seasoning Coatings",
    examples: "Doritos · Cheetos · Takis · Funyuns",
    realWorld: "PepsiCo (Frito-Lay) announced commitment to remove FD&C dyes by 2027 (Apr 2025).",
    shape: "rect",
    note: "Snack seasonings already use a lot of paprika oleoresin and annatto naturally — the synthetic dye load is usually for 'red dust' (Flamin' Hot, Takis) where Red 40 is layered on top of paprika for intensity. Switching to pure paprika + lycopene loses ~30% of the neon-red top note. Brand identity question, not formulation.",
    synthetic: [
      { name: "Red 40 nacho-cheese tint", hex: "#E14D2A" },
      { name: "Yellow 6 cheese", hex: "#F2A035" },
      { name: "Y5 + Y6 nacho", hex: "#E4922E" },
      { name: "Red 40 flamin' hot", hex: "#D2222A" },
    ],
    natural: [
      { name: "Paprika + lycopene red", hex: "#B8472A" },
      { name: "Paprika + annatto cheese", hex: "#D8841B" },
      { name: "Paprika + β-carotene nacho", hex: "#CA7C28" },
      { name: "Paprika oleoresin (no Red 40)", hex: "#A83A28" },
    ],
  },
  {
    category: "Sports / Functional Beverages",
    examples: "Gatorade · Powerade · Vitamin Water · Crystal Light",
    realWorld: "PepsiCo + Coca-Cola both publicly committed to reformulating sports drink portfolio with natural colors by 2027.",
    shape: "bottle",
    note: "Beverages at pH 3.0–4.0 — anthocyanins are stable. Beverages over pH 4.5 (some cream sodas, milk-based) need different chemistry. Light exposure is the killer — clear PET bottle + retail shelf light fades natural colors 20–40% over 6 months.",
    synthetic: [
      { name: "Red 40 fruit punch", hex: "#E03050" },
      { name: "Yellow 5 lemon-lime", hex: "#D9D825" },
      { name: "Blue 1 cool blue", hex: "#0F95C8" },
      { name: "Red 40 + Yellow 6 orange", hex: "#EE7A1E" },
    ],
    natural: [
      { name: "Black carrot anthocyanin punch", hex: "#A92F4F" },
      { name: "Turmeric + safflower lemon-lime", hex: "#C8C42A" },
      { name: "Spirulina + butterfly pea blue", hex: "#246FA0" },
      { name: "Lycopene + paprika orange", hex: "#C76A1B" },
    ],
  },
  {
    category: "Frosted Baked Goods",
    examples: "Pop-Tarts · Donuts · Sprinkle decorations · Cupcake frosting",
    realWorld: "Kellogg's reformulating Pop-Tarts kids' portfolio 2025–2027. Sprinkle category leader (Wilton, Sweets Indeed) launched natural-dye lines 2024.",
    shape: "drip",
    note: "Frosting is high-sugar, low water — protects natural colors from microbial attack. But baking heat above 350°F can dull turmeric and spirulina. Cold-applied frostings work best for natural-color sprinkles.",
    synthetic: [
      { name: "Red 40 strawberry", hex: "#E04060" },
      { name: "Yellow 5 lemon", hex: "#F9D920" },
      { name: "Blue 1 birthday", hex: "#1F8FCB" },
      { name: "Red 40 + Blue 1 pink", hex: "#E97AB0" },
    ],
    natural: [
      { name: "Beet juice strawberry", hex: "#B73A57" },
      { name: "Turmeric lemon", hex: "#E5C424" },
      { name: "Spirulina birthday blue", hex: "#2A75A8" },
      { name: "Beet juice (diluted) pink", hex: "#D88AAA" },
    ],
  },
];

// ─────────────────────────────────────────────────────────────────────────
// Visual primitive: render a small "candy-like" colored swatch tuned to
// the application shape (round dragee, cereal loop, gum-drop, rectangle,
// bottle, frosting drip).
// ─────────────────────────────────────────────────────────────────────────
function ApplicationSwatch({ hex, shape, size = 44 }) {
  const common = {
    width: size,
    height: shape === "rect" ? size * 0.7 : size,
    backgroundColor: hex,
    boxShadow: "inset 0 -3px 6px rgba(0,0,0,0.18), inset 0 3px 4px rgba(255,255,255,0.25)",
  };
  if (shape === "circle") {
    return <div className="ring-1 ring-black/10" style={{ ...common, borderRadius: "50%" }} title={hex} />;
  }
  if (shape === "loop") {
    return (
      <div className="ring-1 ring-black/10 relative" style={{ ...common, borderRadius: "50%" }} title={hex}>
        <div className="absolute" style={{ inset: "30%", borderRadius: "50%", backgroundColor: "#fff" }} />
      </div>
    );
  }
  if (shape === "drop") {
    return <div className="ring-1 ring-black/10" style={{ ...common, borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%" }} title={hex} />;
  }
  if (shape === "drip") {
    return <div className="ring-1 ring-black/10" style={{ ...common, borderRadius: "60% 60% 40% 40% / 60% 60% 40% 40%" }} title={hex} />;
  }
  if (shape === "bottle") {
    return <div className="ring-1 ring-black/10" style={{ ...common, borderRadius: "8px 8px 14px 14px" }} title={hex} />;
  }
  // rect
  return <div className="ring-1 ring-black/10" style={{ ...common, borderRadius: "6px" }} title={hex} />;
}

function Swatch({ hex, size = 44, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div
        className="rounded-md ring-1 ring-black/10 shadow-sm"
        style={{ backgroundColor: hex, width: size, height: size }}
        title={hex}
        data-testid={`swatch-${hex.replace("#", "")}`}
      />
      {label ? <div className="text-[10px] text-stone-600 font-mono">{hex}</div> : null}
    </div>
  );
}

function NotifyForm() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/hue/notify", { email, source: "hue_page" });
      // Local mirror so the user sees they're on the list even if cookies cleared.
      try {
        const list = JSON.parse(localStorage.getItem("color_switch_interest") || "[]");
        list.push({ email, ts: new Date().toISOString() });
        localStorage.setItem("color_switch_interest", JSON.stringify(list));
      } catch (_) { /* ignore */ }
      setSubmitted(true);
      toast.success(data?.message || "You're on the early-access list");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't save — try again");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-4 flex items-center gap-2 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-4 py-3"
        data-testid="notify-success">
        <Check size={18} weight="bold" />
        <span className="text-sm font-medium">You're on the early-access list. We'll be in touch.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-3" data-testid="notify-form">
      <Input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@yourbrand.com"
        className="flex-1"
        data-testid="notify-email-input"
      />
      <Button type="submit"
        disabled={busy}
        className="bg-stone-900 hover:bg-stone-800 text-white"
        data-testid="notify-submit-button">
        {busy ? "Saving…" : <>Notify me <ArrowRight size={14} className="ml-1.5" weight="bold" /></>}
      </Button>
    </form>
  );
}

export default function ColorSwitch() {
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="alaines-hue-page">

        {/* HERO */}
        <div className="relative overflow-hidden rounded-3xl border border-stone-200 bg-gradient-to-br from-stone-50 via-rose-50/30 to-amber-50/40 p-8 sm:p-12 mb-10">
          <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-full text-xs uppercase tracking-wider text-amber-900 font-bold"
            data-testid="under-construction-badge">
            <Wrench size={12} weight="bold" />
            Under Construction
          </div>

          <div className="flex items-center gap-2 text-emerald-800 mb-3">
            <Palette size={22} weight="duotone" />
            <div className="text-xs uppercase tracking-[0.25em] font-semibold">Coming to Alaine's Bench</div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-stone-900 leading-[1.05] tracking-tight">
            Alaine's Hue<span className="text-emerald-800">™</span>
          </h1>
          <p className="mt-3 text-lg sm:text-xl text-stone-700 leading-relaxed max-w-3xl">
            The natural-color reformulator for solo CPG founders.
            <span className="block mt-1 text-base text-stone-600">
              Find the right natural alternative for every banned dye — with stability profiles,
              cost impact, and label-ready ingredient statements.
            </span>
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-2 text-[11px]">
            <span className="px-2.5 py-1 bg-stone-900 text-white rounded-full font-mono">FDA Red 3 — Banned 2027</span>
            <span className="px-2.5 py-1 bg-stone-900 text-white rounded-full font-mono">RFK/HHS — 6 dyes phasing out</span>
            <span className="px-2.5 py-1 bg-stone-900 text-white rounded-full font-mono">CA AB 418 + School Food Safety Act</span>
          </div>

          <NotifyForm />
          <p className="text-[11px] text-stone-500 mt-2 leading-snug">
            We'll notify you when Alaine's Hue™ launches. No spam, no marketing list rental.
          </p>
        </div>

        {/* THE CRISIS — why this matters */}
        <Card className="p-6 border-amber-200 bg-amber-50/40 mb-10" data-testid="crisis-card">
          <div className="flex items-start gap-3">
            <Warning size={20} weight="duotone" className="text-amber-700 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-stone-900">The reformulation cliff is real</h2>
              <ul className="mt-2 text-sm text-stone-800 space-y-1.5 leading-relaxed list-disc list-inside">
                <li><b>Jan 2025:</b> FDA finalized the ban on FD&C Red No. 3. Mandatory removal by January 2027.</li>
                <li><b>Apr 2025:</b> HHS &amp; FDA announced phase-out of Red 40, Yellow 5, Yellow 6, Blue 1, Blue 2, Green 3 from the U.S. food supply.</li>
                <li><b>Oct 2024:</b> California School Food Safety Act banned 6 artificial dyes from CA school food.</li>
                <li><b>Active now:</b> Kraft Heinz, General Mills, Nestlé, Tyson, McCormick, Hershey, PepsiCo all publicly reformulating.</li>
              </ul>
              <p className="mt-3 text-[13px] text-stone-700 italic">
                Big brands have $50M reformulation budgets and Fortune-500 consultants.
                Small-to-medium brands have nothing. <b>That's where Alaine's Hue™ lives.</b>
              </p>
            </div>
          </div>
        </Card>

        {/* DYE → NATURAL SWAP CARDS */}
        <div className="mb-10">
          <h2 className="text-2xl sm:text-3xl font-black text-stone-900 mb-1">
            Every banned dye. The natural alternatives that actually work.
          </h2>
          <p className="text-sm text-stone-600 mb-6">
            Side-by-side visual swatches with food-science notes. Final tool will include
            ΔE color matching, application matrix, and cost-impact calculator.
          </p>

          <div className="grid sm:grid-cols-2 gap-5">
            {DYE_SWAPS.map((row) => (
              <Card key={row.dye} className="p-5 border-stone-200" data-testid={`dye-card-${row.dye.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                <div className="flex items-center gap-4">
                  <Swatch hex={row.dyeHex} size={56} />
                  <div>
                    <div className="font-bold text-stone-900">{row.dye}</div>
                    <div className="text-[11px] text-stone-500 font-mono uppercase tracking-wider">
                      a.k.a. {row.aka}
                    </div>
                    <div className="mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-900 rounded border border-rose-300">
                      {row.status}
                    </div>
                    {row.typicalUseRate ? (
                      <div className="mt-1.5 text-[10px] text-stone-600 font-mono">
                        Typical dye use rate: <b>{row.typicalUseRate}</b>
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className="my-4 flex items-center gap-2 text-stone-400 text-xs uppercase tracking-wider">
                  <div className="h-px flex-1 bg-stone-200" />
                  natural swap options
                  <div className="h-px flex-1 bg-stone-200" />
                </div>
                <div className="space-y-3">
                  {row.naturals.map((n) => (
                    <div key={n.name} className="flex items-start gap-3">
                      <Swatch hex={n.hex} size={36} />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-stone-900">{n.name}</div>
                        {n.ratio ? (
                          <div className="mt-0.5 text-[11px] text-emerald-800 font-mono font-bold">
                            ⇄ Replace at: {n.ratio}
                          </div>
                        ) : null}
                        <div className="text-[11px] text-stone-500 mt-0.5 leading-snug">{n.note}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {row.labMade ? (
                  <div className="mt-4 p-3 rounded-md bg-sky-50/60 border border-sky-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Flask size={14} className="text-sky-800" weight="duotone" />
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-sky-900">
                        Lab-Made / Nature-Identical Option
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Swatch hex={row.labMade.hex} size={32} />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-stone-900">{row.labMade.name}</div>
                        {row.labMade.ratio && row.labMade.ratio !== "N/A" ? (
                          <div className="mt-0.5 text-[11px] text-sky-900 font-mono font-bold">
                            ⇄ Replace at: {row.labMade.ratio}
                          </div>
                        ) : (
                          <div className="mt-0.5 text-[11px] text-stone-500 font-mono italic">
                            No commercial nature-identical synth yet
                          </div>
                        )}
                        <div className="text-[11px] text-stone-600 mt-0.5 leading-snug">{row.labMade.note}</div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </Card>
            ))}
          </div>
        </div>

        {/* STABILITY MATRIX */}
        <Card className="p-6 border-stone-200 mb-10" data-testid="stability-matrix-card">
          <h2 className="text-2xl font-black text-stone-900 mb-1">Stability matrix</h2>
          <p className="text-sm text-stone-600 mb-5">
            What works where. Pick the right natural color based on your product's pH, process heat,
            and shelf-life conditions.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b-2 border-stone-200">
                  <th className="py-2 pr-4 font-bold">Natural Color</th>
                  <th className="py-2 pr-4 font-bold">pH Range</th>
                  <th className="py-2 pr-4 font-bold">Heat</th>
                  <th className="py-2 pr-4 font-bold">Light</th>
                  <th className="py-2 pr-4 font-bold">Cost vs Dye</th>
                </tr>
              </thead>
              <tbody>
                {STABILITY_MATRIX.map((r) => (
                  <tr key={r.color} className="border-b border-stone-100">
                    <td className="py-2.5 pr-4 font-semibold text-stone-900">{r.color}</td>
                    <td className="py-2.5 pr-4 font-mono text-stone-700">{r.pH}</td>
                    <td className="py-2.5 pr-4 text-stone-700">{r.heat}</td>
                    <td className="py-2.5 pr-4 text-stone-700">{r.light}</td>
                    <td className="py-2.5 pr-4 font-mono text-stone-700">{r.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-stone-500 mt-3 leading-relaxed">
            Costs are wholesale natural-color ingredient cost vs. equivalent FD&amp;C dye dosing.
            Real-world ingredient cost impact at the SKU level depends on your dye usage rate
            (mg per serving). Full calculator coming with Alaine's Hue™ launch.
          </p>
        </Card>

        {/* SNACK APPLICATION GALLERY — finished-product visual comparison */}
        <div className="mb-10">
          <h2 className="text-2xl sm:text-3xl font-black text-stone-900 mb-1">
            Snack applications · before and after
          </h2>
          <p className="text-sm text-stone-600 mb-6 max-w-3xl">
            Real reformulation visual comparisons. Synthetic-dye color palettes on the left,
            natural-color replacement palettes on the right — styled like the actual snack format
            (hard-shell, cereal loop, gum-drop, frosting drip, beverage). Use these as your
            starting visual brief for snack reformulation work.
          </p>

          <div className="space-y-5">
            {SNACK_APPLICATIONS.map((app) => (
              <Card key={app.category} className="p-5 border-stone-200"
                data-testid={`snack-app-${app.category.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-bold">Application Category</div>
                    <h3 className="text-lg font-bold text-stone-900 mt-0.5">{app.category}</h3>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      e.g., {app.examples}
                    </div>
                  </div>
                  <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 max-w-md leading-snug">
                    <b>Real-world:</b> {app.realWorld}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mt-4">
                  {/* Synthetic side */}
                  <div className="bg-rose-50/40 border border-rose-200 rounded-md p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-rose-900 mb-3">
                      Synthetic Dyes (Original)
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      {app.synthetic.map((c) => (
                        <div key={c.name} className="flex flex-col items-center gap-1.5 max-w-[80px]">
                          <ApplicationSwatch hex={c.hex} shape={app.shape} size={44} />
                          <div className="text-[10px] text-stone-700 text-center leading-tight">{c.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Natural side */}
                  <div className="bg-emerald-50/40 border border-emerald-200 rounded-md p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-900 mb-3">
                      Natural Reformulation
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      {app.natural.map((c) => (
                        <div key={c.name} className="flex flex-col items-center gap-1.5 max-w-[80px]">
                          <ApplicationSwatch hex={c.hex} shape={app.shape} size={44} />
                          <div className="text-[10px] text-stone-700 text-center leading-tight">{c.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-[12px] text-stone-700 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
                  <b className="text-stone-900">Food-science note:</b> {app.note}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* BRANDS TRANSITIONING */}
        <Card className="p-6 border-stone-200 mb-10" data-testid="brands-card">
          <h2 className="text-2xl font-black text-stone-900 mb-1">Brands already in the queue</h2>
          <p className="text-sm text-stone-600 mb-4">
            A short list of US brands that have publicly committed to natural-color reformulation
            or are actively rolling out new SKUs. The race to clean labels is well underway.
          </p>
          <div className="flex flex-wrap gap-2">
            {TRANSITIONING_BRANDS.map((b) => (
              <span key={b}
                className="px-3 py-1.5 bg-stone-100 border border-stone-200 rounded-full text-[12px] text-stone-700">
                {b}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-stone-400 mt-4 italic">
            Brand names referenced for educational purposes only. Alaine's Bench™ has no
            affiliation with these brands.
          </p>
        </Card>

        {/* FOOTER CTA */}
        <Card className="p-8 border-emerald-700 bg-gradient-to-br from-emerald-50 to-stone-50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
                Be first when Alaine's Hue™ goes live.
              </h3>
              <p className="text-sm text-stone-600 mt-1 max-w-xl">
                Early access opens to Alaine's Bench™ Pro subscribers first.
                Built by a food scientist for founders who want expert guidance at their own pace.
              </p>
            </div>
            <div className="w-full sm:w-auto sm:min-w-[320px]">
              <NotifyForm />
            </div>
          </div>
        </Card>

      </div>
    </Layout>
  );
}
